/**
 * Jetons visuels de NDRC Labs — **dérivés**, jamais inventés.
 *
 * Toute valeur de ce fichier est copiée octet pour octet de `docs/design/desktop-reference.md`,
 * lui-même extrait de la maquette « CRM NovaFit » choisie par l'enseignant le 6 septembre 2026.
 * Une divergence entre ce fichier, `theme.css` et le fichier de référence est un défaut, pas un
 * choix : on corrige le code, jamais la référence.
 *
 * Une app lit ces jetons (ou, plus souvent, les variables CSS que `theme.css` en dérive) ; elle
 * n'écrit jamais une couleur en dur.
 */

/** Polices. Le monospace ne sert qu'à trois choses : l'horloge, les nombres alignés en colonne,
 * les raccourcis clavier. Partout ailleurs, Instrument Sans. */
export const FONTS = {
  ui: '"Instrument Sans", ui-sans-serif, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

/** Quatre niveaux d'encre, deux de trait, quatre de fond. */
export const SURFACES = {
  ink: '#1A1B19',
  ink2: '#3D3F3A',
  muted: '#76786F',
  faint: '#9A9C93',
  line: '#E9E9E4',
  lineSoft: '#F1F1EC',
  panel: '#FAFAF8',
  hover: '#F4F4F1',
  surface: '#FFFFFF',
  sel: '#EFEFEA',
} as const;

/** Forme et profondeur : trois couches d'ombre pour la fenêtre, deux pour le dock, pas d'ombre
 * ailleurs. */
export const SHAPE = {
  radius: '14px',
  shadowWin:
    '0 1px 1px rgba(20,22,18,.04), 0 8px 20px rgba(20,22,18,.08), 0 32px 64px -12px rgba(20,22,18,.22)',
  /** Les deux premières couches de `shadowWin`, à l'octet près : ce que porte une fenêtre du
   * second plan depuis le multi-fenêtres (7 septembre 2026). La troisième couche est retirée, pas
   * remplacée — aucune valeur nouvelle n'entre dans la palette. */
  shadowWinBehind: '0 1px 1px rgba(20,22,18,.04), 0 8px 20px rgba(20,22,18,.08)',
  shadowDock: '0 2px 8px rgba(20,22,18,.10), 0 12px 32px rgba(20,22,18,.16)',
} as const;

/**
 * Le fond d'écran sauge et argile : quatre dégradés radiaux sur un dégradé linéaire. `bodyBase`
 * est posé sous le tout pour que rien ne transparaisse pendant le chargement.
 */
export const WALLPAPER = {
  bodyBase: '#8E9C93',
  background: [
    'radial-gradient(58% 48% at 14% 12%, #C8D2C2 0%, rgba(200, 210, 194, 0) 62%)',
    'radial-gradient(52% 44% at 86% 8%, #D9C9AC 0%, rgba(217, 201, 172, 0) 60%)',
    'radial-gradient(64% 60% at 78% 92%, #9FB8B4 0%, rgba(159, 184, 180, 0) 66%)',
    'radial-gradient(50% 50% at 26% 88%, #C7AFA2 0%, rgba(199, 175, 162, 0) 62%)',
    'linear-gradient(158deg, #B7C3B4 0%, #9CAEA9 46%, #A9A395 100%)',
  ],
} as const;

/**
 * Onze paires « teinte + encre ». Chaque paire est un fond très clair (`tint`) et une encre foncée
 * (`ink`) qui se lit dessus. Elles ne servent **jamais** de décor : uniquement à porter un sens —
 * un statut, une catégorie, une température de prospect.
 */
export const TINTS = {
  amber: { tint: '#FEF3C7', ink: '#B45309' },
  teal: { tint: '#CCFBF1', ink: '#0F766E' },
  rose: { tint: '#FCE7F3', ink: '#BE185D' },
  blue: { tint: '#DBEAFE', ink: '#1D4ED8' },
  cyan: { tint: '#CFFAFE', ink: '#0E7490' },
  fuch: { tint: '#FAE8FF', ink: '#A21CAF' },
  indigo: { tint: '#E0E7FF', ink: '#4338CA' },
  red: { tint: '#FFE4E6', ink: '#BE123C' },
  emer: { tint: '#D1FAE5', ink: '#047857' },
  viol: { tint: '#EDE9FE', ink: '#6D28D9' },
  oran: { tint: '#FFEDD5', ink: '#C2410C' },
} as const;

export type TintName = keyof typeof TINTS;

export const TINT_NAMES = Object.keys(TINTS) as readonly TintName[];

/** Tous les jetons en un seul objet — ce que `theme.css` publie sous forme de variables CSS. */
export const THEME_TOKENS = {
  fonts: FONTS,
  surfaces: SURFACES,
  shape: SHAPE,
  wallpaper: WALLPAPER,
  tints: TINTS,
} as const;

/** La paire neutre : encre `--ink-2` sur `--sel`, la seule combinaison de la référence qui ne
 * porte aucun sens particulier. C'est là que retombe une valeur inconnue. */
const NEUTRAL_PAIR = { tint: SURFACES.sel, ink: SURFACES.ink2 } as const;

/** Index inverse : `#0f766e` → la paire teal, `#ccfbf1` → la paire teal aussi. Construit une fois,
 * en minuscules, pour que la casse du manifeste n'ait aucune importance. */
const PAIR_BY_HEX = new Map<string, { tint: string; ink: string }>(
  TINT_NAMES.flatMap((name) => {
    const pair = TINTS[name];
    return [
      [pair.tint.toLowerCase(), pair],
      [pair.ink.toLowerCase(), pair],
    ] as ReadonlyArray<[string, { tint: string; ink: string }]>;
  }),
);

/**
 * Résout une couleur fournie par une donnée (le `color` d'un manifeste, un statut de contact) vers
 * sa paire « teinte + encre ».
 *
 * **Deux écritures sont acceptées, et c'est délibéré** : le nom de la paire (`'teal'`) et
 * n'importe laquelle de ses deux valeurs hexadécimales (`'#0F766E'`, `'#CCFBF1'`, casse
 * indifférente). Un manifeste écrit naturellement la seconde forme — il copie la valeur qu'il lit
 * dans `docs/design/desktop-reference.md` — et la première version de cette fonction ne
 * reconnaissait que la première : `resolveTint('#0F766E')` retombait sur la paire neutre, et les
 * deux apps du dock sortaient **grises**. C'était le défaut signalé par l'enseignant le 7 septembre
 * 2026 (« pourquoi est-ce que le tableur et les icônes du CRM sont tous en gris »).
 *
 * Une valeur inconnue ne casse rien et ne devine rien : elle retombe sur la paire neutre. Une
 * couleur hors palette n'est donc jamais affichée — la palette reste verrouillée, même si une app
 * écrit n'importe quoi dans son manifeste.
 */
export function resolveTint(name: string | undefined): { tint: string; ink: string } {
  if (name === undefined) return NEUTRAL_PAIR;
  if (Object.hasOwn(TINTS, name)) return TINTS[name as TintName];
  return PAIR_BY_HEX.get(name.toLowerCase()) ?? NEUTRAL_PAIR;
}
